import ViewSearchBar from "./ViewSearchBar";
import ViewTabBar from "./ViewTabBar";
import { useEffect, useRef, useState } from "react";
import { ViewHeaderContext } from "components/utils/Contexts";

// Parameter definition
const WIDTH_LIMIT = 954; // px

interface ViewHeaderProps {
  searchQuery: string;
  activeItem: string;
  tabOptions: string[];
  showTabOptions: boolean;
  showSchoolsDropdown?: boolean;
  setActiveItem: React.Dispatch<React.SetStateAction<string>>;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  filters?: (ref: React.RefObject<HTMLDivElement>) => JSX.Element | undefined;
  headerRef: React.MutableRefObject<any>;
}

const ViewHeader: React.FC<ViewHeaderProps> = ({
  searchQuery,
  activeItem,
  tabOptions,
  showTabOptions,
  showSchoolsDropdown = false,
  setActiveItem,
  setSearchQuery,
  filters,
  headerRef,
}) => {
  // States definition
  const [panelWidth, setPanelWidth] = useState(Infinity);
  const [format, setFormat] = useState<"expanded" | "shrinked">("expanded");

  // Reference to this element
  const divRef = useRef<HTMLDivElement>(null);
  // Define a reference for the filter container
  const filterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Set the initial value
    if (divRef.current) {
      setPanelWidth(divRef.current.offsetWidth);
    }

    // Optionally, add a resize event listener to update width on window resize
    const handleResize = () => {
      if (divRef.current) {
        setPanelWidth(divRef.current.offsetWidth);
      }
    };

    window.addEventListener("resize", handleResize);

    // Clean up the resize listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  /**
   * Function to define the format of the dropdown list based on the width
   */
  useEffect(() => {
    // Get the filter size (if defined)
    let filterWidth = 0;
    if (filterRef.current){
      filterWidth = filterRef.current.offsetWidth || 0;
    }

    if (
      panelWidth < WIDTH_LIMIT + filterWidth &&
      showSchoolsDropdown
    )
      setFormat("shrinked");
    else setFormat("expanded");
  }, [panelWidth, activeItem]);

  return (
    <ViewHeaderContext.Provider value={{ format }}>
      <div
        ref={divRef}
        className="self-stretch justify-between items-center inline-flex gap-4"
      >
        {showTabOptions && tabOptions.length > 0 && (
          <ViewTabBar
            activeItem={activeItem}
            tabOptions={tabOptions}
            showSchoolsDropdown={showSchoolsDropdown}
            setActiveItem={setActiveItem}
            filtersPanel={filters ? filters(filterRef) : undefined}
            headerRef={headerRef}
          />
        )}
        <ViewSearchBar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          showTabOptions={showTabOptions && tabOptions.length > 0}
          format={format}
        />
      </div>
    </ViewHeaderContext.Provider>
  );
};

export default ViewHeader;
